import { Injectable } from '@angular/core';
import { ResourceType } from '@app/core/model/resource-type';
import { InstrumentPlatforms, InstrumentTypes } from '@app/core/model/runs/instrument';
import { IRun } from '@app/core/model/runs/run';
import { REQUEUE_SUPPORTED_RUN_STATUSES } from '@app/core/model/runs/run-status';
import { REQUEUE_NOT_SUPPORTED_WORKFLOWS } from '@app/core/model/runs/workflow';
import { IResourceCapabilities } from '@app/core/model/v2-api/v2-api-wrappers';
import { BaseService } from '@app/core/services/base.service';
import { BsApiService } from '@app/core/services/bs-api/bs-api-service';
import { UserRestrictionService } from '@app/core/services/user/user-restriction.service';
import { Observable, of } from 'rxjs';
import { catchError, map, tap } from 'rxjs/operators';
import { ILibrary } from '@models/library/library';
import { BasespaceService, V2CreateExistingLibraryLabRequeueRequest, V2LabRequeue } from '@bssh/ng-sdk';
import { IRequeueParams } from '../../bsshapi/api-wrappers';
import { Router } from '@angular/router';
import { GssApiService } from '@app/run-planning/services/gss-api-service';
import { TransformSampleSheetResponse } from '@stratus/gss-ng-sdk';
import { CookieService } from 'ngx-cookie-service';
import { COOKIE_FOR_REQUEUE } from '@app/run-planning/constants';
import { IPool } from '@app/core/model/pool/pool';
import { MULTI_ANALYSIS_RUN_SETUP_PATH } from '@app/cloud-run-prep/constants';

// Interface for the service.
export interface IRequeueService {
  canRequeue(run: IRun, userId: string): boolean;
  openRequeueSampleSheetPage(run: IRun);
  canRequeueLibrary(library: ILibrary, userId: string): boolean;
  requeueLibrary(library: ILibrary, requeueParams: IRequeueParams): Observable<V2LabRequeue>;
}

@Injectable({
  providedIn: 'root'
})
export class RequeueService extends BaseService implements IRequeueService {
  public requeueRunContent;

  constructor(private bsApiService: BsApiService, private basespaceService: BasespaceService,
              private userRestrictionService: UserRestrictionService,
              private cookieService: CookieService,
              private router: Router,
              private gssApiService: GssApiService) {
    super();
  }

  public canRequeue(run: IRun, userId: string): boolean {
    return !!(run && !run.IsArchived &&
      run.UserOwnedBy.Id === userId &&
      !this.userRestrictionService.userHasStorageRestrictions() &&
      this.canRequeueAnalysis(run) &&
      !this.isPrepTabRun(run));
  }

  canRequeueLibrary(library: ILibrary, userId: string): boolean {
    if (library == null || userId == null) {
      return false;
    }

    return library.BioSample != null && library.Project != null &&
      library.Project.UserOwnedBy != null && library.Project.UserOwnedBy.Id === userId &&
      library.BioSample.UserOwnedBy != null && library.BioSample.UserOwnedBy.Id === userId;
  }

  requeueLibrary(library: ILibrary, requeueParams: IRequeueParams): Observable<V2LabRequeue> {
    const payload: V2CreateExistingLibraryLabRequeueRequest = {
      RequestedAdditionalYield: requeueParams.requestedAdditionalYield,
      // already done --> Math.round(requeueParams.requestedAdditionalYield * requeueParams.currentBasePairUnit.divisor),
      LIMSId: null,
    };
    const params: BasespaceService.PostV2LibrariesLibraryidLabrequeuesParams = {
      libraryid: library.Id,
      payload
    };
    return this.basespaceService.PostV2LibrariesLibraryidLabrequeues(params);
  }

  /**
   * Returns true when all the below conditions are passed, otherwise false.
   * 1. If the run status is supported for requeue. [[RunStatus.REQUEUE_SUPPORTED_RUN_STATUSES]]
   * 2. {@link isRequeueSupportedForInstrument}
   * 3. {@link isRequeueSupportedForWorkflow}
   *
   * @param run The run
   */
  private canRequeueAnalysis(run: IRun): boolean {
    return REQUEUE_SUPPORTED_RUN_STATUSES.includes(run.Status ? run.Status.trim() : run.Status) &&
      this.isRequeueSupportedForInstrument(run) &&
      this.isRequeueSupportedForWorkflow(run.Workflow);
  }

  /**
   * Returns true if the run's instrument type is supported for requeue, otherwise false.
   */
  private isRequeueSupportedForInstrument(run: IRun): boolean {
      const resolvedInstrumentType = run && (run.InstrumentType || run.Instrument && run.Instrument.Type);
      return !!(resolvedInstrumentType &&
        !InstrumentTypes.REQUEUE_NOT_SUPPORTED_INSTRUMENT_TYPES.includes(resolvedInstrumentType));
  }

  /**
   * Returns true if the workflow is not [[AnalysisWorkflowType.NO_ANALYSIS]], otherwise false.
   * @param workflow workflow of the run.
   */
  // TODO: why do we need to check AnalysisWorkflowTypeDescription and AnalysisWorkflowType
  // Understand the API response value for workflow and keep only one class.
  private isRequeueSupportedForWorkflow(workflow: string): boolean {
    return !!(workflow && !REQUEUE_NOT_SUPPORTED_WORKFLOWS.includes(workflow));
  }

  /**
   * Returns true if the specified run is Prep tab run, otherwise false.
   *
   * @param run run object
   */
  private isPrepTabRun(run): boolean {
    return (run &&
      run.Instrument &&
      (run.Instrument.PlatformName === InstrumentPlatforms.NEXTSEQ ||
      run.Instrument.PlatformName === InstrumentPlatforms.MINISEQ) &&
      run.PrepSettings != null)
  }

  public openRequeueSampleSheetPage(run: IRun) {
    this.router.navigate(['run', run.V1Pre3Id, run.ExperimentName, 'requeue']);
  }

  public loadRunPlanningRequeue(run: IRun, requeueRunContent: TransformSampleSheetResponse): void {
    const runName = `${run.ExperimentName || run.Name}_requeue`;
    const runId = run.V1Pre3Id || run.Id;

    // Set cookie and requeue content
    this.cookieService.set(COOKIE_FOR_REQUEUE.bsshRequeueRunName, runName,
      null, COOKIE_FOR_REQUEUE.path, COOKIE_FOR_REQUEUE.domain, false, "Strict");
    this.requeueRunContent = requeueRunContent;

    // Navigate to run planning
    this.router.navigate([MULTI_ANALYSIS_RUN_SETUP_PATH, runId], { queryParams: { requeue: true } });
  }

  public requeueRunWithoutValidation(run: IRun, sampleSheetContent: string, analysisId?: string): Observable<any> {
    return this.bsApiService.requeueSampleSheet(run.V1Pre3Id, sampleSheetContent, analysisId);
  }

  public canRequeuePool(pool: IPool, userId: string): boolean {
    if (pool == null || userId == null) {
      return false;
    }
    return pool.UserOwnedBy.Id === userId;
  }
}
